// components/leads/LeadDetailModal.js
// Modal dettaglio lead completo

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Phone, Mail, MapPin, Calendar, User, Edit, Clock, FileText, 
  ArrowRight, CheckCircle, ExternalLink, Download, Loader2, FileCheck, Trash2, MessageSquare
} from 'lucide-react';
import { formatDate, formatDateTime, formatCurrency, leadStatusLabels, leadStatusColors, getBadgeClass } from '../../lib/utils';

const workflowSteps = [
  { status: 'nuovo', label: 'Nuovo', color: 'bg-blue-500' },
  { status: 'contattato', label: 'Contattato', color: 'bg-cyan-500' },
  { status: 'assegnato', label: 'Assegnato', color: 'bg-indigo-500' },
  { status: 'appuntamento_fissato', label: 'Appuntamento', color: 'bg-purple-500' },
  { status: 'sopralluogo_effettuato', label: 'Sopralluogo', color: 'bg-violet-500' },
  { status: 'in_preventivazione', label: 'Preventivazione', color: 'bg-amber-500' },
  { status: 'preventivo_presentato', label: 'Presentato', color: 'bg-orange-500' },
  { status: 'in_trattativa', label: 'Trattativa', color: 'bg-rose-500' },
  { status: 'in_contratto', label: 'Contratto', color: 'bg-emerald-500' },
  { status: 'convertito', label: 'Convertito', color: 'bg-green-600' },
];

const validTransitions = {
  nuovo: ['contattato', 'non_risponde', 'perso'],
  contattato: ['assegnato', 'non_risponde', 'perso'],
  non_risponde: ['contattato', 'perso'],
  assegnato: ['appuntamento_fissato', 'perso'],
  appuntamento_fissato: ['sopralluogo_effettuato', 'perso'],
  sopralluogo_effettuato: ['in_preventivazione', 'perso'],
  in_preventivazione: ['preventivo_presentato', 'perso'],
  preventivo_presentato: ['in_trattativa', 'perso'],
  in_trattativa: ['in_contratto', 'perso'],
  in_contratto: ['convertito', 'perso'],
};

const transitionLabels = {
  contattato: 'Segna Contattato',
  non_risponde: 'Non Risponde',
  assegnato: 'Assegna Tecnico',
  appuntamento_fissato: 'Fissa Appuntamento',
  sopralluogo_effettuato: 'Sopralluogo Fatto',
  in_preventivazione: 'Inizia Preventivo',
  preventivo_presentato: 'Preventivo Presentato',
  in_trattativa: 'In Trattativa',
  in_contratto: 'Prepara Contratto',
  convertito: 'Chiudi Vinto',
  perso: 'Segna Perso',
};

const LeadDetailModal = ({
  lead,
  onClose,
  isEditing,
  editData,
  setEditData,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  savingLead,
  leadEstimates,
  loadingEstimates,
  leadContract,
  loadingContract,
  onOpenEstimate,
  onAssign,
  onTransition,
  getTechnicianName,
  isAdmin,
  onDelete
}) => {
  const navigate = useNavigate();

  if (!lead) return null;

  const getWorkflowProgress = (status) => {
    const index = workflowSteps.findIndex(s => s.status === status);
    if (index === -1) return 0;
    return ((index + 1) / workflowSteps.length) * 100;
  };

  const getNextActions = () => {
    const transitions = validTransitions[lead.status] || [];
    return transitions.map(t => ({
      status: t,
      label: transitionLabels[t] || leadStatusLabels[t] || t,
      isNegative: t === 'perso' || t === 'non_risponde',
      needsTechnician: t === 'assegnato'
    }));
  };

  const handleDownloadContract = async () => {
    try {
      const response = await fetch(
        `/api/contracts/${leadContract.id}/pdf`,
        { withCredentials: true }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Contratto_${leadContract.contract_number || 'download'}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading contract:', error);
    }
  };

  return (
    <Dialog open={!!lead} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Fixed Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0 pr-12">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {isEditing ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={editData?.first_name || ''}
                      onChange={(e) => setEditData({...editData, first_name: e.target.value})}
                      placeholder="Nome"
                      className="h-9"
                    />
                    <Input
                      value={editData?.last_name || ''}
                      onChange={(e) => setEditData({...editData, last_name: e.target.value})}
                      placeholder="Cognome"
                      className="h-9"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <DialogTitle className="text-xl">
                    {lead.first_name} {lead.last_name}
                  </DialogTitle>
                  <DialogDescription className="flex items-center gap-2 mt-1">
                    <Badge className={getBadgeClass(leadStatusColors[lead.status])}>
                      {leadStatusLabels[lead.status] || lead.status}
                    </Badge>
                    {lead.source && (
                      <span className="text-xs text-[#999]">via {lead.source}</span>
                    )}
                  </DialogDescription>
                </>
              )}
            </div>
            {!isEditing && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 ml-2"
                onClick={onStartEdit}
              >
                <Edit className="w-3.5 h-3.5 mr-1" />
                Modifica
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Progress */}
          {!isEditing && !['perso', 'non_risponde'].includes(lead.status) && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs text-[#737373] mb-2">
                <span>Avanzamento</span>
                <span>{Math.round(getWorkflowProgress(lead.status))}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#FF7819] to-[#FF9F5A] transition-all"
                  style={{ width: `${getWorkflowProgress(lead.status)}%` }}
                />
              </div>
            </div>
          )}

          {/* Contact Info */}
          <div className="space-y-3 mb-4">
            {isEditing ? (
              <div className="grid grid-cols-2 gap-3">
                <Input
                  value={editData?.phone || ''}
                  onChange={(e) => setEditData({...editData, phone: e.target.value})}
                  placeholder="Telefono"
                />
                <Input
                  value={editData?.email || ''}
                  onChange={(e) => setEditData({...editData, email: e.target.value})}
                  placeholder="Email"
                />
                <Input
                  value={editData?.property_address || ''}
                  onChange={(e) => setEditData({...editData, property_address: e.target.value})}
                  placeholder="Indirizzo"
                  className="col-span-2"
                />
                <Input
                  value={editData?.property_city || ''}
                  onChange={(e) => setEditData({...editData, property_city: e.target.value})}
                  placeholder="Città"
                />
                <Input
                  value={editData?.property_cap || ''}
                  onChange={(e) => setEditData({...editData, property_cap: e.target.value})}
                  placeholder="CAP"
                />
              </div>
            ) : (
              <>
                {lead.phone && (
                  <a href={`tel:${lead.phone}`} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Phone className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-[#3C3C3C]">{lead.phone}</span>
                  </a>
                )}
                {lead.email && (
                  <a href={`mailto:${lead.email}`} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Mail className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-[#3C3C3C]">{lead.email}</span>
                  </a>
                )}
                {lead.property_address && (
                  <div className="flex items-center gap-3 p-2">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <span className="text-[#3C3C3C]">{lead.property_address}</span>
                      {(lead.property_city || lead.property_cap) && (
                        <p className="text-xs text-[#737373]">
                          {lead.property_city} {lead.property_cap}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {lead.assigned_technician_id && (
                  <div className="flex items-center gap-3 p-2">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-amber-600" />
                    </div>
                    <span className="text-[#3C3C3C]">{getTechnicianName(lead.assigned_technician_id)}</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Notes Section */}
          {!isEditing && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-[#3C3C3C] mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-600" />
                Note lead ({Array.isArray(lead.notes) ? lead.notes.length : 0})
              </h4>
              {(() => {
                const notes = Array.isArray(lead.notes) ? lead.notes : [];
                const sortedNotes = [...notes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                const displayNotes = sortedNotes.slice(0, 3);
                
                if (notes.length === 0) {
                  return (
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <p className="text-sm text-[#737373]">Nessuna nota inserita</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-2">
                    {displayNotes.map((note, index) => (
                      <div 
                        key={note.id || index}
                        className="p-3 bg-gray-50 rounded-lg border border-gray-100"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Clock className="w-3 h-3 text-[#737373]" />
                          <span className="text-xs text-[#FF7819] font-medium">
                            {formatDateTime(note.created_at)}
                          </span>
                          {note.created_by_name && (
                            <span className="text-xs text-[#737373]">
                              - {note.created_by_name}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#3C3C3C] line-clamp-2">{note.text}</p>
                      </div>
                    ))}
                    {notes.length > 3 && (
                      <Button
                        variant="link"
                        size="sm"
                        className="w-full text-[#FF7819] hover:text-[#E56A10]"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                      >
                        Vedi tutte ({notes.length} note)
                      </Button>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Estimates Section */}
          {!isEditing && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-[#3C3C3C] mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-amber-600" />
                Preventivi ({leadEstimates.length})
              </h4>
              {loadingEstimates ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-[#FF7819]" />
                </div>
              ) : leadEstimates.length > 0 ? (
                <div className="space-y-2">
                  {leadEstimates.map(est => (
                    <div
                      key={est.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                      onClick={() => navigate(`/estimates/${est.id}`)}
                    >
                      <div>
                        <p className="font-medium text-[#3C3C3C]">{est.estimate_number}</p>
                        <p className="text-xs text-[#737373]">{formatDate(est.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-[#FF7819]">{formatCurrency(est.total)}</p>
                        <Badge className="text-xs" variant="outline">{est.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => onOpenEstimate(lead.id)}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Crea Preventivo
                </Button>
              )}
            </div>
          )}

          {/* Contract Section */}
          {!isEditing && ['in_contratto', 'convertito'].includes(lead.status) && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-[#3C3C3C] mb-2">Contratto</h4>
              {loadingContract ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-[#FF7819]" />
                </div>
              ) : leadContract ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-emerald-800">{leadContract.contract_number}</p>
                      <p className="text-xs text-emerald-600">{formatDate(leadContract.created_at)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                        onClick={() => navigate(`/contracts/${leadContract.id}`)}
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1" />
                        Apri
                      </Button>
                      {leadContract.pdf_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                          onClick={handleDownloadContract}
                        >
                          <Download className="w-3.5 h-3.5 mr-1" />
                          PDF
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <FileCheck className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-[#737373]">Nessun contratto associato</p>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          {!isEditing && !['perso', 'convertito'].includes(lead.status) && (
            <div className="pt-4 border-t">
              <p className="text-sm font-medium text-[#3C3C3C] mb-3">Azioni disponibili</p>
              <div className="flex flex-wrap gap-2">
                {getNextActions().map(action => (
                  <Button
                    key={action.status}
                    variant={action.isNegative ? "outline" : "default"}
                    size="sm"
                    className={action.isNegative 
                      ? 'border-red-300 text-red-600 hover:bg-red-50' 
                      : 'bg-[#FF7819] hover:bg-[#E56A10] text-white'
                    }
                    onClick={() => {
                      if (action.needsTechnician) {
                        onAssign();
                      } else {
                        onTransition({ lead, action });
                      }
                    }}
                  >
                    <ArrowRight className="w-3.5 h-3.5 mr-1.5" />
                    {action.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Fixed Footer */}
        <DialogFooter className="px-6 py-4 border-t shrink-0 bg-gray-50">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={onCancelEdit} disabled={savingLead}>
                Annulla
              </Button>
              <Button 
                size="sm" 
                className="bg-[#FF7819] hover:bg-[#E56A10] text-white"
                onClick={onSaveEdit}
                disabled={savingLead}
              >
                {savingLead ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Salva Modifiche
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              {isAdmin && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  onClick={onDelete}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={onClose}>
                Chiudi
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`/calendar?lead=${lead.id}`)}>
                <Calendar className="w-4 h-4 mr-2" />
                Calendario
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LeadDetailModal;
